// =========================================
// db.local.js — store reattivo locale (localStorage)
// =========================================
//
// Backend 100% client-side (nessun Firebase). I dati vivono in localStorage
// sotto un'unica chiave JSON, organizzati per tenant e collezione:
//
//   { tenants: { [tenantId]: { assets:{[id]:doc}, locations:{...},
//                              sensorInstances:{...}, movements:{...} } } }
//
// Le collezioni sono mappe id→doc per upsert/lookup O(1). I lettori (hooks)
// ricevono array. Ogni mutazione persiste e notifica i subscriber → le viste
// si aggiornano "live" come con onSnapshot.
//
// Tutto è SSR-safe: durante il render server (no window) lo store opera su uno
// stato in memoria vuoto e non tocca localStorage.
//
// Questo è l'implementazione LOCAL: il router db.js la seleziona quando
// Firebase non è configurato (vedi firebase/config.js).

const STORAGE_KEY = 'iotilize_at_db_v1';
const COLLECTIONS = ['assets', 'locations', 'sensorInstances', 'movements'];

const hasWindow = () => typeof window !== 'undefined';

let state = { tenants: {} };
const listeners = new Set();

function load() {
  if (!hasWindow()) return { tenants: {} };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { tenants: {} };
  } catch {
    return { tenants: {} };
  }
}

function persist() {
  if (!hasWindow()) return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota */ }
}

function emit() { listeners.forEach(fn => { try { fn(); } catch { /* ignore */ } }); }

// Carica lo stato persistito al primo import lato client.
if (hasWindow()) state = load();

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function ensureTenant(tenantId) {
  if (!tenantId) return;
  if (!state.tenants[tenantId]) {
    state.tenants[tenantId] = COLLECTIONS.reduce((acc, c) => { acc[c] = {}; return acc; }, {});
    persist();
  }
}

function coll(tenantId, name) {
  if (!tenantId || !state.tenants[tenantId]) return {};
  if (!state.tenants[tenantId][name]) state.tenants[tenantId][name] = {};
  return state.tenants[tenantId][name];
}

// ── Reads ────────────────────────────────────────────────────────────────────
export function getCollection(tenantId, name) {
  return Object.values(coll(tenantId, name));
}

export function getDocById(tenantId, name, id) {
  return coll(tenantId, name)[id] || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────
// Upsert con id esplicito (merge shallow se esiste già).
export function setDoc(tenantId, name, id, data) {
  ensureTenant(tenantId);
  const c = state.tenants[tenantId][name] || (state.tenants[tenantId][name] = {});
  const prev = c[id] || {};
  c[id] = { ...prev, ...data, id, createdAt: prev.createdAt || data.createdAt || Date.now(), updatedAt: Date.now() };
  persist();
  emit();
  return c[id];
}

// Update parziale (no-op se non esiste).
export function updateDoc(tenantId, name, id, partial) {
  ensureTenant(tenantId);
  const c = state.tenants[tenantId][name] || (state.tenants[tenantId][name] = {});
  if (!c[id]) return null;
  c[id] = { ...c[id], ...partial, updatedAt: Date.now() };
  persist();
  emit();
  return c[id];
}

export function deleteDoc(tenantId, name, id) {
  const c = coll(tenantId, name);
  if (c[id]) { delete c[id]; persist(); emit(); }
}

// Storico misurazioni: non disponibile in locale (nessun cron di telemetria).
export async function getReadings() { return []; }

// Reset completo di un tenant (svuota tutte le collezioni).
export function resetTenant(tenantId) {
  state.tenants[tenantId] = COLLECTIONS.reduce((acc, c) => { acc[c] = {}; return acc; }, {});
  persist();
  emit();
}
